import React, { useEffect, useState } from "react";
import { Offcanvas, Form, Button, Alert, Spinner } from "react-bootstrap";
import ReactSelect from "react-select";
import { Icon } from "../../../utils/iconHelper";
import { useEntityLabels } from "../../../_context/EntityLabelsContext";
import ProgramManagementService, {
  ProgramFormData,
  ProgramLookups,
} from "../../../_services/ProgramManagementService";

interface Props {
  show: boolean;
  onHide: () => void;
  programId: number | null;
  lookups?: ProgramLookups;
  onSuccess: () => void;
}

const emptyForm: ProgramFormData = {
  name: "",
  webFormDisplayName: null,
  transcriptDisplayName: null,
  programShortCode: null,
  programShortCode2: null,
  certificateCode: null,
  versionId: null,
  minDob: null,
  maxDob: null,
  noOfAcademicYears: null,
  noOfBatches: null,
  openForRegistration: 0,
  enableStatus: 1,
  sortOrder: 0,
  enquiryType2Restriction: null,
  censusDuration: null,
  contractDuration: null,
  schoolIds: [],
  streamIds: [],
  testCategoryIds: [],
  universityIds: [],
  learningOptionIds: [],
  disciplineIds: [],
};

const ProgramFormPanel: React.FC<Props> = ({ show, onHide, programId, lookups, onSuccess }) => {
  const { label } = useEntityLabels();
  const programNoun = (label("program", "Program").replace(/\s*Management\s*$/i, "") || "Program");
  const empty: ProgramLookups = {
    years: [], terms: [], courses: [], schools: [], programTypes: [],
    streams: [], testCategories: [], universities: [], learningOptions: [], disciplines: [],
  };
  const lk: ProgramLookups = lookups || empty;
  const [formData, setFormData] = useState<ProgramFormData>({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = programId !== null;

  useEffect(() => {
    if (!show) return;
    if (programId) {
      loadProgram(programId);
    } else {
      setFormData({ ...emptyForm });
      setError(null);
    }
  }, [programId, show]);

  const loadProgram = async (id: number) => {
    setFetching(true);
    setError(null);
    const r = await ProgramManagementService.getProgramById(id);
    if (r.success && r.data) {
      const p = r.data;
      setFormData({
        name: p.name || "",
        webFormDisplayName: p.webFormDisplayName,
        transcriptDisplayName: p.transcriptDisplayName,
        programShortCode: p.programShortCode,
        programShortCode2: p.programShortCode2,
        certificateCode: p.certificateCode,
        versionId: p.versionId,
        minDob: p.minDob ? String(p.minDob).slice(0, 10) : null,
        maxDob: p.maxDob ? String(p.maxDob).slice(0, 10) : null,
        noOfAcademicYears: p.noOfAcademicYears,
        noOfBatches: p.noOfBatches,
        openForRegistration: p.openForRegistration ?? 0,
        enableStatus: p.enableStatus ?? 1,
        sortOrder: p.sortOrder ?? 0,
        enquiryType2Restriction: p.enquiryType2Restriction,
        censusDuration: p.censusDuration,
        contractDuration: p.contractDuration,
        schoolIds: p.schoolIds || [],
        streamIds: p.streamIds || [],
        testCategoryIds: p.testCategoryIds || [],
        universityIds: p.universityIds || [],
        learningOptionIds: p.learningOptionIds || [],
        disciplineIds: p.disciplineIds || [],
      });
    } else {
      setError(r.message || `Failed to load ${programNoun.toLowerCase()}`);
    }
    setFetching(false);
  };

  const setField = (k: keyof ProgramFormData, v: any) => {
    setFormData((prev) => ({ ...prev, [k]: v }));
    setError(null);
  };
  const setNumberField = (k: keyof ProgramFormData, raw: string) => {
    setField(k, raw === "" ? null : Number(raw));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    setLoading(true);
    try {
      const r = isEditMode
        ? await ProgramManagementService.updateProgram(programId!, formData)
        : await ProgramManagementService.createProgram(formData);
      if (r.success) {
        onSuccess();
      } else {
        setError(r.message || `Failed to save ${programNoun.toLowerCase()}`);
      }
    } catch (err: any) {
      setError(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "720px" }}
      className="pm-form-panel"
    >
      <Offcanvas.Header closeButton className="border-bottom py-3">
        <Offcanvas.Title className="h6 mb-0">
          {isEditMode ? "Edit" : "Create"} {programNoun}
        </Offcanvas.Title>
      </Offcanvas.Header>
      {fetching ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner role="status" animation="border" variant="primary" />
          <span className="ms-2">Loading...</span>
        </div>
      ) : (
        <Form onSubmit={handleSubmit} className="d-flex flex-column h-100 overflow-hidden">
          <Offcanvas.Body className="flex-grow-1 overflow-auto">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2 px-3 mb-3 small">
                {error}
              </Alert>
            )}

            <h6 className="text-muted small fw-bold text-uppercase mb-2">Basic</h6>
            <div className="mb-3">
              <Form.Label className="small mb-1 fw-medium">Name <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" size="sm" value={formData.name} onChange={(e) => setField("name", e.target.value)} required />
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Web Form Display Name</Form.Label>
                <Form.Control type="text" size="sm" value={formData.webFormDisplayName ?? ""} onChange={(e) => setField("webFormDisplayName", e.target.value || null)} />
              </div>
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Transcript Display Name</Form.Label>
                <Form.Control type="text" size="sm" value={formData.transcriptDisplayName ?? ""} onChange={(e) => setField("transcriptDisplayName", e.target.value || null)} />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">Short Code</Form.Label>
                <Form.Control type="text" size="sm" value={formData.programShortCode ?? ""} onChange={(e) => setField("programShortCode", e.target.value || null)} />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">Short Code 2</Form.Label>
                <Form.Control type="text" size="sm" value={formData.programShortCode2 ?? ""} onChange={(e) => setField("programShortCode2", e.target.value || null)} />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">Certificate Code</Form.Label>
                <Form.Control type="text" size="sm" value={formData.certificateCode ?? ""} onChange={(e) => setField("certificateCode", e.target.value || null)} />
              </div>
            </div>

            <h6 className="text-muted small fw-bold text-uppercase mb-2 mt-4">Structure</h6>
            <div className="row mb-3">
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">No. of Academic Years</Form.Label>
                <Form.Control type="number" size="sm" min="0" value={formData.noOfAcademicYears ?? ""} onChange={(e) => setNumberField("noOfAcademicYears", e.target.value)} />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">No. of Batches</Form.Label>
                <Form.Control type="number" size="sm" min="0" value={formData.noOfBatches ?? ""} onChange={(e) => setNumberField("noOfBatches", e.target.value)} />
              </div>
              <div className="col-4">
                <Form.Label className="small mb-1 fw-medium">Sort Order</Form.Label>
                <Form.Control type="number" size="sm" min="0" value={formData.sortOrder ?? 0} onChange={(e) => setNumberField("sortOrder", e.target.value)} />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Min DOB</Form.Label>
                <Form.Control type="date" size="sm" value={formData.minDob ?? ""} onChange={(e) => setField("minDob", e.target.value || null)} />
              </div>
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Max DOB</Form.Label>
                <Form.Control type="date" size="sm" value={formData.maxDob ?? ""} onChange={(e) => setField("maxDob", e.target.value || null)} />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Open For Registration</Form.Label>
                <Form.Select size="sm" value={formData.openForRegistration ?? 0} onChange={(e) => setField("openForRegistration", Number(e.target.value))}>
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </Form.Select>
              </div>
              {isEditMode && (
                <div className="col-6">
                  <Form.Label className="small mb-1 fw-medium">Status</Form.Label>
                  <Form.Select size="sm" value={formData.enableStatus ?? 1} onChange={(e) => setField("enableStatus", Number(e.target.value))}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Select>
                </div>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.enquiryType2Restriction ?? ""}
                  onChange={(e) => setField("enquiryType2Restriction", e.target.value || null)}
                >
                  <option value="">-- select --</option>
                  {lk.programTypes.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-3">
                <Form.Label className="small mb-1 fw-medium">Census Duration (days)</Form.Label>
                <Form.Control type="number" size="sm" min="0" value={formData.censusDuration ?? ""} onChange={(e) => setNumberField("censusDuration", e.target.value)} />
              </div>
              <div className="col-3">
                <Form.Label className="small mb-1 fw-medium">Contract Duration (months)</Form.Label>
                <Form.Control type="number" size="sm" min="0" value={formData.contractDuration ?? ""} onChange={(e) => setNumberField("contractDuration", e.target.value)} />
              </div>
            </div>

            <h6 className="text-muted small fw-bold text-uppercase mb-2 mt-4">{label("school", "BU")}</h6>
            <div className="row mb-3">
              <div className="col-12">
                <Form.Label className="small mb-1 fw-medium">Mapped {label("school", "BU")}(s)</Form.Label>
                <ReactSelect
                  isMulti
                  placeholder={`Select ${label("school", "BU")}(s)...`}
                  options={lk.schools.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.schools.filter((s) => (formData.schoolIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("schoolIds", (opts || []).map((o: any) => o.value))}
                />
                <Form.Text className="text-muted">
                  Controls which {label("school", "BU")}s can use this {programNoun.toLowerCase()} when creating batches.
                </Form.Text>
              </div>
            </div>

            <h6 className="text-muted small fw-bold text-uppercase mb-2 mt-4">Categorization</h6>
            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Stream</Form.Label>
                <ReactSelect
                  isMulti
                  options={lk.streams.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.streams.filter((s) => (formData.streamIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("streamIds", (opts || []).map((o: any) => o.value))}
                />
              </div>
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Test Category</Form.Label>
                <ReactSelect
                  isMulti
                  options={lk.testCategories.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.testCategories.filter((s) => (formData.testCategoryIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("testCategoryIds", (opts || []).map((o: any) => o.value))}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">University</Form.Label>
                <ReactSelect
                  isMulti
                  options={lk.universities.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.universities.filter((s) => (formData.universityIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("universityIds", (opts || []).map((o: any) => o.value))}
                />
              </div>
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Learning Options</Form.Label>
                <ReactSelect
                  isMulti
                  options={lk.learningOptions.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.learningOptions.filter((s) => (formData.learningOptionIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("learningOptionIds", (opts || []).map((o: any) => o.value))}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-6">
                <Form.Label className="small mb-1 fw-medium">Discipline</Form.Label>
                <ReactSelect
                  isMulti
                  options={lk.disciplines.map((s) => ({ value: s.id, label: s.name }))}
                  value={lk.disciplines.filter((s) => (formData.disciplineIds || []).includes(s.id)).map((s) => ({ value: s.id, label: s.name }))}
                  onChange={(opts: any) => setField("disciplineIds", (opts || []).map((o: any) => o.value))}
                />
              </div>
            </div>
          </Offcanvas.Body>
          <div className="border-top p-3 bg-light d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={onHide} disabled={loading}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit" disabled={loading}>
              {loading ? <Spinner role="status" animation="border" size="sm" /> : <><Icon.Save size={14} className="me-1" />{isEditMode ? "Update" : "Create"}</>}
            </Button>
          </div>
        </Form>
      )}
    </Offcanvas>
  );
};

export default ProgramFormPanel;
